import { useState, useEffect } from 'react';
import { getComprasPorProduto, getComprasPorDepartamento, getComprasNotasFiscais } from '../services/api';
import { PageHeader, Loading, Erro, Badge } from '../components/UI';
import FiltroData from '../components/FiltroData';
import './Vendas.css';

const fmt  = v => (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtN = v => (v ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtData = v => v ? String(v).split('T')[0] : '—';

const ABAS = [
  { id: 'produto',      label: 'Por Produto' },
  { id: 'departamento', label: 'Por Departamento' },
  { id: 'notas',        label: 'Notas Fiscais' },
];

// ─── Bar Chart ────────────────────────────────────────────────────────────────

function BarChart({ dados, labelKey, valueKey, qtyKey }) {
  if (!dados?.length) return <div className="chart-vazio">Sem dados para o período.</div>;
  const max = Math.max(...dados.map(d => parseFloat(d[valueKey]) || 0), 0.01);
  return (
    <div className="chart-bars">
      {dados.map((d, i) => {
        const pct = (parseFloat(d[valueKey]) / max) * 100;
        return (
          <div key={i} className="chart-row">
            <span className="chart-label chart-label--wide">{d[labelKey]}</span>
            <div className="chart-track">
              <div className="chart-bar" style={{ width: `${Math.max(pct, 1)}%` }} />
            </div>
            <div className="chart-meta">
              <span className="chart-value">{fmt(d[valueKey])}</span>
              {qtyKey && <span className="chart-qty">{fmtN(d[qtyKey])} un.</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Aba: Por Produto ─────────────────────────────────────────────────────────

function TabProduto({ periodo }) {
  const [dados, setDados] = useState([]);
  const [load,  setLoad]  = useState(false);
  const [erro,  setErro]  = useState('');

  useEffect(() => {
    setLoad(true); setErro('');
    getComprasPorProduto(periodo.di, periodo.df)
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setLoad(false));
  }, [periodo.di, periodo.df]);

  const total = dados.reduce((s, d) => s + (parseFloat(d.TCOMPRA) || 0), 0);

  return (
    <div className="vnd-card">
      <div className="vnd-card-titulo">Compras por Produto</div>
      {erro && <Erro mensagem={erro} onRetry={() => {}} />}
      {load ? <Loading /> : (
        <div className="vnd-tbl-wrap">
          <table className="vnd-tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Código</th>
                <th>Produto</th>
                <th className="vnd-right">Qtd</th>
                <th className="vnd-right">Total Compra</th>
                <th className="vnd-right">Custo Médio Unit.</th>
              </tr>
            </thead>
            <tbody>
              {dados.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-dim)' }}>Sem dados para o período.</td></tr>
              )}
              {dados.map((d, i) => (
                <tr key={i}>
                  <td className="vnd-rank">{i + 1}</td>
                  <td style={{ color: 'var(--text-dim)', fontSize: 12 }}>{d.COD1 || '—'}</td>
                  <td><strong>{d.PRODUTO || '—'}</strong></td>
                  <td className="vnd-right">{fmtN(d.QTD)}</td>
                  <td className="vnd-right vnd-bold">{fmt(d.TCOMPRA)}</td>
                  <td className="vnd-right vnd-dim">{fmt(d.CUSTO_MEDIO)}</td>
                </tr>
              ))}
              {dados.length > 0 && (
                <tr style={{ background: 'var(--bg-base)', fontWeight: 700 }}>
                  <td colSpan={4} style={{ padding: '10px 14px', fontSize: 13 }}>Total</td>
                  <td className="vnd-right vnd-bold" style={{ padding: '10px 14px' }}>{fmt(total)}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Aba: Por Departamento ────────────────────────────────────────────────────

function TabDepartamento({ periodo }) {
  const [dados, setDados] = useState([]);
  const [load,  setLoad]  = useState(false);
  const [erro,  setErro]  = useState('');

  useEffect(() => {
    setLoad(true); setErro('');
    getComprasPorDepartamento(periodo.di, periodo.df)
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setLoad(false));
  }, [periodo.di, periodo.df]);

  const total = dados.reduce((s, d) => s + (parseFloat(d.TCOMPRA) || 0), 0);

  return (
    <div className="vnd-card">
      <div className="vnd-card-titulo">Compras por Departamento</div>
      {erro && <Erro mensagem={erro} onRetry={() => {}} />}
      {load ? <Loading /> : (
        <>
          <BarChart dados={dados} labelKey="DEPT_NM" valueKey="TCOMPRA" qtyKey="QTD" />
          {dados.length > 0 && (
            <div className="vnd-tbl-wrap">
              <table className="vnd-tbl">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Departamento</th>
                    <th className="vnd-right">Qtd</th>
                    <th className="vnd-right">Total Compra</th>
                  </tr>
                </thead>
                <tbody>
                  {dados.map((d, i) => (
                    <tr key={i}>
                      <td className="vnd-rank">{i + 1}</td>
                      <td><strong>{d.DEPT_NM || '—'}</strong></td>
                      <td className="vnd-right">{fmtN(d.QTD)}</td>
                      <td className="vnd-right vnd-bold">{fmt(d.TCOMPRA)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: 'var(--bg-base)', fontWeight: 700 }}>
                    <td colSpan={3} style={{ padding: '10px 14px', fontSize: 13 }}>Total</td>
                    <td className="vnd-right vnd-bold" style={{ padding: '10px 14px' }}>{fmt(total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Aba: Notas Fiscais ───────────────────────────────────────────────────────

function situacaoBadge(sit) {
  if (sit === 100) return <Badge valor="Autorizada"  tipo="success" />;
  if (sit === 101) return <Badge valor="Cancelada"   tipo="danger"  />;
  if (sit === 110) return <Badge valor="Denegada"    tipo="warning" />;
  return <Badge valor={sit ?? '—'} tipo="default" />;
}

function lancamentoBadge(status) {
  if (status == null) return <span style={{ color: 'var(--text-dim)' }}>—</span>;
  if (status === 1)   return <Badge valor="Lançada"       tipo="success" />;
  if (status === 2)   return <Badge valor="Contabilizada" tipo="success" />;
  return <Badge valor={status} tipo="default" />;
}

function TabNotas({ periodo }) {
  const [dados, setDados] = useState([]);
  const [load,  setLoad]  = useState(false);
  const [erro,  setErro]  = useState('');

  useEffect(() => {
    setLoad(true); setErro('');
    getComprasNotasFiscais(periodo.di, periodo.df)
      .then(setDados)
      .catch(e => setErro(e.message))
      .finally(() => setLoad(false));
  }, [periodo.di, periodo.df]);

  const total = dados.reduce((s, d) => s + (parseFloat(d.VALOR) || 0), 0);

  return (
    <div className="vnd-card">
      <div className="vnd-card-titulo">Notas Fiscais Recebidas</div>
      {erro && <Erro mensagem={erro} onRetry={() => {}} />}
      {load ? <Loading /> : (
        <div className="vnd-tbl-wrap">
          <table className="vnd-tbl">
            <thead>
              <tr>
                <th>#ID</th>
                <th>Emissão</th>
                <th>Situação</th>
                <th>Emitente</th>
                <th>CNPJ</th>
                <th>NF</th>
                <th>Série</th>
                <th className="vnd-right">Valor</th>
                <th>Lançamento</th>
                <th>Entrada</th>
                <th>Contabilizada</th>
                <th>Operador</th>
              </tr>
            </thead>
            <tbody>
              {dados.length === 0 && (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: '32px', color: 'var(--text-dim)' }}>Sem notas fiscais no período.</td></tr>
              )}
              {dados.map((d, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 700, color: 'var(--laranja)' }}>{d.ID}</td>
                  <td>{fmtData(d.EMISSAO)}</td>
                  <td>{situacaoBadge(d.SITUACAO)}</td>
                  <td><strong>{d.EMITENTE_RAZAO || '—'}</strong></td>
                  <td style={{ fontSize: 12, color: 'var(--text-dim)' }}>{d.EMITENTE_CNPJ || '—'}</td>
                  <td>{d.NFE_NUMERO || '—'}</td>
                  <td>{d.NFE_SERIE || '—'}</td>
                  <td className="vnd-right vnd-bold">{fmt(d.VALOR)}</td>
                  <td>{lancamentoBadge(d.LANCAMENTO_STATUS)}</td>
                  <td>{fmtData(d.DATA_ENTRADA)}</td>
                  <td>{fmtData(d.DATA_CONTABILIZADA)}</td>
                  <td style={{ fontSize: 12 }}>{d.OPERADORNOME || '—'}</td>
                </tr>
              ))}
              {dados.length > 0 && (
                <tr style={{ background: 'var(--bg-base)', fontWeight: 700 }}>
                  <td colSpan={7} style={{ padding: '10px 14px', fontSize: 13 }}>
                    Total — {dados.length} nota{dados.length !== 1 ? 's' : ''}
                  </td>
                  <td className="vnd-right vnd-bold" style={{ padding: '10px 14px' }}>{fmt(total)}</td>
                  <td colSpan={4} />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function Compras() {
  const [periodo, setPeriodo] = useState(null);
  const [aba,     setAba]     = useState('produto');

  return (
    <div>
      <PageHeader titulo="Compras" subtitulo={periodo?.label || ''}>
        <FiltroData onChange={setPeriodo} defaultModo="mes" />
      </PageHeader>

      <div className="tabs-bar">
        {ABAS.map(a => (
          <button
            key={a.id}
            className={`tab-btn${aba === a.id ? ' tab-btn--ativo' : ''}`}
            onClick={() => setAba(a.id)}
          >
            {a.label}
          </button>
        ))}
      </div>

      {periodo?.di && (
        <>
          {aba === 'produto'      && <TabProduto      periodo={periodo} />}
          {aba === 'departamento' && <TabDepartamento periodo={periodo} />}
          {aba === 'notas'        && <TabNotas        periodo={periodo} />}
        </>
      )}
    </div>
  );
}
